import { useMemo } from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import type { AxisDomain } from "recharts/types/util/types";

import { ChartLegend, ChartLegendContent } from "@/components/ui/ChartLegend";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartColorCssVarKey,
} from "@/components/ui/ChartTooltip";
import {
  CHART_VALUE_AXIS_TOP_MARGIN_PX,
  ColumnChartValueYAxis,
  formatChartValueAxisTick,
  useColumnChartValueAxis,
} from "@/components/ui/columnChartValueAxis";
import { useReportTheme } from "@/components/ui/report/ReportThemeProvider";
import { cn } from "@/lib/utils";
import { formatCategoryAxisTick } from "@/lib/utils/chartAxisTickFormatter";
import { isChartHexColor } from "@/lib/utils/chartUtils";
import { resolveChartShowLegend } from "@/lib/utils/resolveChartShowLegend";
import { stackedBarRadius } from "@/lib/utils/stackedBarRadius";

export type ComposedChartDatum = Record<string, string | number | undefined>;

/** Single mixed series when using the catalog / unified API (bar, line, area on same axes). */
export type ComposedChartSeriesItem = {
  key: string;
  type: "bar" | "line" | "area";
  label?: string;
};

export type ComposedBarSeriesItem = {
  key: string;
  label?: string;
};

export type ComposedLineSeriesItem = {
  key: string;
  label?: string;
};

export type ComposedChartViewProps = {
  data: ComposedChartDatum[];
  chartConfig: ChartConfig;
  /** Category axis field (time buckets, labels). */
  xAxisKey: string;
  /**
   * Preferred: bar, line, and area series with explicit types (render order: area → bar → line).
   * When set, {@link barSeries} and {@link lineSeries} are ignored.
   */
  series?: ComposedChartSeriesItem[];
  /** @deprecated Prefer {@link series} — bar series only. */
  barSeries?: ComposedBarSeriesItem[];
  /** @deprecated Prefer {@link series} — line series only. */
  lineSeries?: ComposedLineSeriesItem[];
  /**
   * Recharts `layout`: `horizontal` = vertical columns; `vertical` = horizontal bars.
   * @default 'horizontal'
   */
  layout?: "horizontal" | "vertical";
  /** When true, bar series share one stack (ignored when using typed {@link series} without multiple bars). */
  stacked?: boolean;
  /**
   * When true, bars + areas use the left Y scale and lines use the right Y scale (column layout only).
   */
  dualAxis?: boolean;
  title?: string | null;
  description?: string | null;
  height?: number | null;
  className?: string;
  /** When set to a positive number, category tick labels are truncated to this length; omit for full labels. */
  xAxisTickChars?: number;
  /** @default true when more than one series total */
  showLegend?: boolean;
  /** @default false */
  hideTooltipLabel?: boolean;
  /**
   * Bar corner radius: columns top only; horizontal bars value end only.
   * Stacked: only the top column segment / outer horizontal segment rounded; base square on axis.
   * @default 4
   */
  radius?: number;
  /** Area fill opacity (composed chart chat spec). @default 0.1 */
  areaFillOpacity?: number;
  /** Line point markers. @default false */
  dot?: boolean;
  isAnimationActive?: boolean;
  accessibilityLayer?: boolean;
};

const ComposedChartEmpty = () => (
  <div className="flex w-full min-w-0 items-center justify-center rounded-md border border-dashed border-muted-foreground/25 py-4 text-xxs text-muted-foreground">
    No data to display
  </div>
);

/**
 * Exactly one bar + one line and no areas: distinct colors when both would use the same token.
 * Skipped when both resolve to an explicit hex, so a requested flat color survives.
 */
const resolveComposedChartConfig = (
  chartConfig: ChartConfig,
  barList: { key: string; label: string }[],
  lineList: { key: string; label: string }[],
  areaList: { key: string; label: string }[],
): ChartConfig => {
  if (areaList.length > 0 || barList.length !== 1 || lineList.length !== 1) {
    return chartConfig;
  }

  const firstBar = barList[0];
  const firstLine = lineList[0];
  if (firstBar === undefined || firstLine === undefined) {
    return chartConfig;
  }

  const barKey = firstBar.key;
  const lineKey = firstLine.key;
  const barCfg = chartConfig[barKey];
  const lineCfg = chartConfig[lineKey];
  const barColor = barCfg?.color;
  const lineColor = lineCfg?.color;

  if (isChartHexColor(barColor) && isChartHexColor(lineColor)) {
    return chartConfig;
  }

  if (barColor != null && lineColor != null && barColor !== lineColor) {
    return chartConfig;
  }

  const nextBarColor = barColor ?? "var(--chart-1)";
  const nextLineColor =
    lineColor != null && lineColor !== nextBarColor
      ? lineColor
      : "var(--chart-2)";

  return {
    ...chartConfig,
    [barKey]: {
      label: barCfg?.label ?? firstBar.label,
      color: nextBarColor,
    },
    [lineKey]: {
      label: lineCfg?.label ?? firstLine.label,
      color: nextLineColor,
    },
  };
};

const ComposedChartView = ({
  data,
  chartConfig,
  xAxisKey,
  series: seriesProp,
  barSeries = [],
  lineSeries = [],
  layout = "horizontal",
  stacked = false,
  dualAxis = false,
  title = null,
  description = null,
  height = null,
  className,
  xAxisTickChars,
  showLegend: showLegendProp,
  hideTooltipLabel = false,
  radius = 4,
  areaFillOpacity = 0.1,
  dot = false,
  isAnimationActive = true,
  accessibilityLayer = true,
}: ComposedChartViewProps) => {
  const inReportContext = useReportTheme() != null;
  const useMixedSeries =
    seriesProp != null && Array.isArray(seriesProp) && seriesProp.length > 0;

  const barList = useMemo(() => {
    if (useMixedSeries) {
      return seriesProp
        .filter((s) => s.type === "bar")
        .map((s) => ({
          key: s.key,
          label: String(chartConfig[s.key]?.label ?? s.label ?? s.key),
        }));
    }
    return barSeries.length > 0
      ? barSeries.map((s) => ({
          key: s.key,
          label: String(chartConfig[s.key]?.label ?? s.label ?? s.key),
        }))
      : [];
  }, [useMixedSeries, seriesProp, barSeries, chartConfig]);

  const lineList = useMemo(() => {
    if (useMixedSeries) {
      return seriesProp
        .filter((s) => s.type === "line")
        .map((s) => ({
          key: s.key,
          label: String(chartConfig[s.key]?.label ?? s.label ?? s.key),
        }));
    }
    return lineSeries.length > 0
      ? lineSeries.map((s) => ({
          key: s.key,
          label: String(chartConfig[s.key]?.label ?? s.label ?? s.key),
        }))
      : [];
  }, [useMixedSeries, seriesProp, lineSeries, chartConfig]);

  const areaList = useMemo(() => {
    if (!useMixedSeries) return [];
    return seriesProp
      .filter((s) => s.type === "area")
      .map((s) => ({
        key: s.key,
        label: String(chartConfig[s.key]?.label ?? s.label ?? s.key),
      }));
  }, [useMixedSeries, seriesProp, chartConfig]);

  const resolvedChartConfig = useMemo(
    () => resolveComposedChartConfig(chartConfig, barList, lineList, areaList),
    [chartConfig, barList, lineList, areaList],
  );

  const allSeriesKeys = useMemo(
    () => [
      ...areaList.map((a) => a.key),
      ...barList.map((b) => b.key),
      ...lineList.map((l) => l.key),
    ],
    [areaList, barList, lineList],
  );

  const isColumnChart = layout === "horizontal";
  const hasBarSeries = barList.length > 0;

  const leftValueKeys = useMemo(
    () =>
      dualAxis && isColumnChart
        ? [...areaList.map((s) => s.key), ...barList.map((s) => s.key)]
        : allSeriesKeys,
    [dualAxis, isColumnChart, areaList, barList, allSeriesKeys],
  );

  const rightValueKeys = useMemo(() => lineList.map((s) => s.key), [lineList]);

  const leftValueAxis = useColumnChartValueAxis(data, {
    autoMinValue: !(dualAxis || hasBarSeries),
    valueKeys: leftValueKeys.length > 0 ? leftValueKeys : undefined,
  });
  const rightValueAxis = useColumnChartValueAxis(data, {
    autoMinValue: true,
    valueKeys: rightValueKeys.length > 0 ? rightValueKeys : undefined,
  });
  const stackId =
    stacked && barList.length > 0 ? "composed-bar-stack" : undefined;
  const nBars = barList.length;

  const hasValues =
    data.length > 0 &&
    allSeriesKeys.length > 0 &&
    data.some((row) =>
      allSeriesKeys.some((key) => {
        const v = row[key];
        const n = typeof v === "number" ? v : Number(v);
        return !Number.isNaN(n);
      }),
    );

  const totalSeriesCount = areaList.length + barList.length + lineList.length;
  const showLegend = resolveChartShowLegend(
    showLegendProp,
    totalSeriesCount,
    inReportContext,
    true,
  );

  if (!hasValues) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-1.5">
        {title != null && title !== "" ? (
          <p className="m-0 text-xs font-medium text-foreground">{title}</p>
        ) : null}
        <ComposedChartEmpty />
      </div>
    );
  }

  const barYAxisId = dualAxis && isColumnChart ? "left" : undefined;
  const lineYAxisId = dualAxis && isColumnChart ? "right" : undefined;
  const areaYAxisId = dualAxis && isColumnChart ? "left" : undefined;

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-1.5"
      style={height != null ? { minHeight: height, height } : undefined}
    >
      {title != null && title !== "" ? (
        <p className="m-0 text-xs font-medium text-foreground">{title}</p>
      ) : null}
      {description != null && description !== "" ? (
        <p className="m-0 text-xs text-muted-foreground">{description}</p>
      ) : null}
      <ChartContainer
        config={resolvedChartConfig}
        className={cn(
          "mx-auto aspect-video w-full justify-center",
          height == null ? "max-h-[240px]" : "max-h-[min(280px,100%)]",
          className,
        )}
      >
        <ComposedChart
          accessibilityLayer={accessibilityLayer}
          data={data}
          layout={layout}
          margin={{
            top: CHART_VALUE_AXIS_TOP_MARGIN_PX,
            left: 12,
            right: dualAxis && isColumnChart ? 8 : 12,
          }}
        >
          {isColumnChart ? (
            <CartesianGrid vertical={false} />
          ) : (
            <CartesianGrid horizontal={false} />
          )}
          {isColumnChart ? (
            <XAxis
              dataKey={xAxisKey}
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tickFormatter={formatCategoryAxisTick(xAxisTickChars)}
            />
          ) : (
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              allowDecimals
              domain={leftValueAxis.yAxisDomain as AxisDomain}
              tickFormatter={formatChartValueAxisTick}
            />
          )}
          {isColumnChart ? (
            dualAxis ? (
              <>
                <ColumnChartValueYAxis
                  yAxisId="left"
                  calculatedYAxisWidth={leftValueAxis.calculatedYAxisWidth}
                  yAxisDomain={leftValueAxis.yAxisDomain}
                  onWidthChange={leftValueAxis.handleYAxisLabelWidthChange}
                />
                <ColumnChartValueYAxis
                  yAxisId="right"
                  orientation="right"
                  calculatedYAxisWidth={rightValueAxis.calculatedYAxisWidth}
                  yAxisDomain={rightValueAxis.yAxisDomain}
                  onWidthChange={rightValueAxis.handleYAxisLabelWidthChange}
                />
              </>
            ) : (
              <ColumnChartValueYAxis
                calculatedYAxisWidth={leftValueAxis.calculatedYAxisWidth}
                yAxisDomain={leftValueAxis.yAxisDomain}
                onWidthChange={leftValueAxis.handleYAxisLabelWidthChange}
              />
            )
          ) : (
            <YAxis
              dataKey={xAxisKey}
              type="category"
              tickLine={false}
              axisLine={false}
              width={56}
              tickFormatter={formatCategoryAxisTick(xAxisTickChars)}
            />
          )}
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel={hideTooltipLabel}
                indicator="dot"
              />
            }
          />
          {showLegend ? (
            <ChartLegend
              verticalAlign="bottom"
              content={
                <ChartLegendContent className="flex-wrap gap-x-2 gap-y-0.5 pt-1.5 text-foreground dark:text-gray-300" />
              }
            />
          ) : null}
          {areaList.map((s) => {
            const stroke = `var(--color-${chartColorCssVarKey(s.key)})`;
            return (
              <Area
                key={s.key}
                dataKey={s.key}
                name={s.label}
                type="monotone"
                stroke={stroke}
                fill={stroke}
                fillOpacity={areaFillOpacity}
                isAnimationActive={isAnimationActive}
                yAxisId={areaYAxisId}
              />
            );
          })}
          {barList.map((s, index) => {
            const fill = `var(--color-${chartColorCssVarKey(s.key)})`;
            const barRadius: [number, number, number, number] = stacked
              ? stackedBarRadius(index, nBars, radius, isColumnChart)
              : isColumnChart
                ? [radius, radius, 0, 0]
                : [0, radius, radius, 0];
            return (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                fill={fill}
                radius={barRadius}
                stackId={stackId}
                yAxisId={barYAxisId}
                isAnimationActive={isAnimationActive}
              />
            );
          })}
          {lineList.map((s) => {
            const stroke = `var(--color-${chartColorCssVarKey(s.key)})`;
            return (
              <Line
                key={s.key}
                dataKey={s.key}
                name={s.label}
                type="monotone"
                stroke={stroke}
                strokeWidth={2}
                dot={dot}
                isAnimationActive={isAnimationActive}
                yAxisId={lineYAxisId}
              />
            );
          })}
        </ComposedChart>
      </ChartContainer>
    </div>
  );
};

export default ComposedChartView;
